import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp, MapPin, ChevronRight, BarChart3, Building2, Users, Wallet } from 'lucide-react';
import { INSIGHTS } from '../data/landingContent';
import { formatIndian, lakhsToIndian } from '../utils/indianFormat';
import { useSiteContent } from '../hooks/useSiteContent';
import { dangerousSafeHtml } from '../utils/sanitizeHtml';

/**
 * India Accommodation Insights page (/insights)
 * Curated, source-cited statistics + the 6 landing-page articles.
 *
 * Stats source: FHRAI India Hotel Industry Report 2024, IBEF Tourism Brief 2025,
 *               Ministry of Tourism (Govt. of India) — Indian Tourism Statistics at a Glance 2024.
 */

// Raw counts (Branded + classified hotel rooms — FHRAI 2024 + state tourism boards)
// Domestic visits in CRORE (Cr) per state — Ministry of Tourism 2023.
const INDIA_STATS = [
  {
    icon: Building2,
    label: 'Branded hotel rooms in India',
    value: formatIndian(198_000),   // 1.98 L
    delta: '+24% since 2022',
    source: 'FHRAI 2024',
  },
  {
    icon: TrendingUp,
    label: 'Domestic tourist visits (2023)',
    value: formatIndian(2_51_00_00_000),  // 251 Cr
    delta: '+44% YoY',
    source: 'Ministry of Tourism',
  },
  {
    icon: Users,
    label: 'Foreign tourist arrivals',
    value: formatIndian(95_00_000),   // 95 L
    delta: '+43.5% YoY (2023)',
    source: 'IBEF 2025',
  },
  {
    icon: Wallet,
    label: 'Indian hospitality market size',
    value: '$32 Bn',
    delta: 'projected $59 Bn by 2028',
    source: 'IBEF 2025',
  },
];

// State-wise classified+branded hotel room inventory (approximate, FHRAI 2024).
// Domestic visits (Cr) per state — Ministry of Tourism 2023.
const STATE_DATA = [
  { state: 'Maharashtra',     rooms: 22000, visitsCr: 17.0 },
  { state: 'Tamil Nadu',      rooms: 19500, visitsCr: 28.6 },
  { state: 'Karnataka',       rooms: 18000, visitsCr: 28.4 },
  { state: 'Rajasthan',       rooms: 17000, visitsCr: 17.9 },
  { state: 'Kerala',          rooms: 14000, visitsCr: 1.92 },
  { state: 'Goa',             rooms: 12500, visitsCr: 0.83 },
  { state: 'Uttar Pradesh',   rooms: 11200, visitsCr: 47.8 },
  { state: 'Delhi NCR',       rooms: 14800, visitsCr: 2.86 },
  { state: 'Punjab',          rooms: 6600,  visitsCr: 4.65 },
  { state: 'West Bengal',     rooms: 6400,  visitsCr: 22.7 },
  { state: 'Gujarat',         rooms: 8200,  visitsCr: 17.5 },
  { state: 'Haryana',         rooms: 4800,  visitsCr: 1.94 },
];

const TOP_CITIES = [
  { city: 'Mumbai',       avg: '₹8,400', occupancy: '78%' },
  { city: 'New Delhi',    avg: '₹7,650', occupancy: '74%' },
  { city: 'Bengaluru',    avg: '₹6,200', occupancy: '72%' },
  { city: 'Goa',          avg: '₹9,100', occupancy: '81%' },
  { city: 'Jaipur',       avg: '₹5,800', occupancy: '69%' },
  { city: 'Chandigarh',   avg: '₹5,200', occupancy: '71%' },
  { city: 'Amritsar',     avg: '₹4,600', occupancy: '68%' },
  { city: 'Gurugram',     avg: '₹7,400', occupancy: '76%' },
  { city: 'Hyderabad',    avg: '₹6,800', occupancy: '73%' },
  { city: 'Chennai',      avg: '₹5,950', occupancy: '70%' },
  { city: 'Pune',         avg: '₹5,400', occupancy: '72%' },
  { city: 'Kolkata',      avg: '₹4,950', occupancy: '67%' },
  { city: 'Udaipur',      avg: '₹7,200', occupancy: '79%' },
  { city: 'Agra',         avg: '₹4,300', occupancy: '66%' },
  { city: 'Varanasi',     avg: '₹4,100', occupancy: '64%' },
];

export const InsightsPage = () => {
  const { sections: c } = useSiteContent('insights');
  return (
    <div className="min-h-screen bg-white" data-testid="insights-page">
      {/* HERO */}
      <section className="relative pt-12 pb-16 bg-gradient-to-b from-[#FFF5F8] via-white to-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FF4D8A] rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-3" data-testid="insights-tag">
            <Sparkles className="h-3.5 w-3.5" /> INSIGHTS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-black mb-3 leading-[1.05]" data-testid="insights-title">
            {c.hero_title || 'India accommodation, in numbers'}
          </h1>
          {c.hero_subtitle_html ? (
            <div
              className="text-base sm:text-lg text-gray-600 max-w-2xl mb-2 prose prose-sm prose-p:my-0"
              dangerouslySetInnerHTML={dangerousSafeHtml(c.hero_subtitle_html)}
              data-testid="insights-subtitle"
            />
          ) : (
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mb-2">
              Real data on India&apos;s hospitality market — sourced from Ministry of Tourism,
              FHRAI and IBEF. Indian numbering system (Lakh / Crore).
            </p>
          )}
          <p className="text-xs text-gray-400">
            Sources: <a className="underline hover:text-[#FF0059]" href="https://www.fhrai.com/" target="_blank" rel="noopener noreferrer">FHRAI</a> · <a className="underline hover:text-[#FF0059]" href="https://www.ibef.org/industry/tourism-hospitality-india" target="_blank" rel="noopener noreferrer">IBEF</a> · <a className="underline hover:text-[#FF0059]" href="https://tourism.gov.in/" target="_blank" rel="noopener noreferrer">Ministry of Tourism</a>
          </p>
        </div>
      </section>

      {/* STATS GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="india-stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {INDIA_STATS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] transition-colors" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}>
                <div className="w-10 h-10 rounded-full bg-[#FFE5EE] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#FF0059]" />
                </div>
                <p className="text-3xl font-black text-black">{s.value}</p>
                <p className="text-sm font-bold text-gray-700 mt-1">{s.label}</p>
                <p className="text-xs text-[#FF0059] font-bold mt-2">{s.delta}</p>
                <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">{s.source}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STATE TABLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-gray-100" data-testid="state-data-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-2">
              <MapPin className="h-3.5 w-3.5" /> STATE-WISE · 2023-24
            </div>
            <h2 className="text-3xl font-black tracking-tight text-black">State-wise hotel inventory &amp; tourist footfall</h2>
            <p className="text-gray-600 mt-2 text-sm">Branded + classified room counts (FHRAI 2024) and domestic visitor totals (Ministry of Tourism 2023).</p>
          </div>
        </div>
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-2xl">
          <table className="w-full text-sm" data-testid="state-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#FAFAFA]">
                <th className="text-left px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">State</th>
                <th className="text-right px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">Branded rooms</th>
                <th className="text-right px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">Domestic visits (2023)</th>
              </tr>
            </thead>
            <tbody>
              {STATE_DATA.map((s) => (
                <tr key={s.state} className="border-b border-gray-100 last:border-0 hover:bg-[#FFF5F8] transition-colors" data-testid={`state-row-${s.state.toLowerCase().replace(/\s+/g, '-')}`}>
                  <td className="px-5 py-3 font-bold text-black flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#FF0059]" /> {s.state}
                  </td>
                  <td className="px-5 py-3 text-right font-black text-black" data-testid={`state-rooms-${s.state.toLowerCase().replace(/\s+/g, '-')}`}>
                    {formatIndian(s.rooms)}
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-[#FF0059]" data-testid={`state-visits-${s.state.toLowerCase().replace(/\s+/g, '-')}`}>
                    {lakhsToIndian(s.visitsCr * 100)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CITY TABLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-gray-100" data-testid="top-cities-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF9DB] text-[#997800] text-xs font-bold mb-2">
              <BarChart3 className="h-3.5 w-3.5" /> CITY BENCHMARKS · 2024
            </div>
            <h2 className="text-3xl font-black tracking-tight text-black">Top {TOP_CITIES.length} Indian cities · avg nightly rate &amp; occupancy</h2>
            <p className="text-gray-600 mt-2 text-sm">Average daily rate (ADR) blended across 3★–5★ branded hotels. Source: FHRAI India Hotel Industry Survey 2024.</p>
          </div>
        </div>
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-2xl">
          <table className="w-full text-sm" data-testid="city-benchmark-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#FAFAFA]">
                <th className="text-left px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">City</th>
                <th className="text-right px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">Avg ₹/night</th>
                <th className="text-right px-5 py-3 font-bold text-gray-600 uppercase text-xs tracking-wider">Occupancy</th>
              </tr>
            </thead>
            <tbody>
              {TOP_CITIES.map((c) => (
                <tr key={c.city} className="border-b border-gray-100 last:border-0 hover:bg-[#FFF5F8] transition-colors" data-testid={`city-row-${c.city.toLowerCase()}`}>
                  <td className="px-5 py-3 font-bold text-black flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#FF0059]" /> {c.city}
                  </td>
                  <td className="px-5 py-3 text-right font-black text-black">{c.avg}</td>
                  <td className="px-5 py-3 text-right font-bold text-[#FF0059]">{c.occupancy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CURATED ARTICLES */}
      <section className="bg-[#FAFAFA] py-16 border-t border-gray-100" data-testid="articles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-3xl font-black tracking-tight text-black">Travel guides &amp; city stories</h2>
            <p className="text-gray-600 mt-2">Curated by Punjab &amp; Haryana locals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INSIGHTS.map((insight) => (
              <article
                key={insight.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer group"
                data-testid={`article-${insight.id}`}
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img src={insight.image} alt={insight.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#FF0059] mb-2">
                    {insight.type === 'trending' ? <TrendingUp className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                    {insight.type === 'trending' ? 'TRENDING' : insight.city.toUpperCase()}
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500 font-medium">{insight.readTime}</span>
                  </div>
                  <h3 className="font-black text-lg text-black leading-tight mb-2 group-hover:text-[#FF0059] transition-colors line-clamp-2">
                    {insight.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{insight.excerpt}</p>
                  <button className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#FF0059] hover:underline">
                    Read article <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#FF0059] to-[#CC0047] p-10 md:p-14 text-white text-center" data-testid="insights-cta">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">{c.cta_title || 'Got a Punjab travel story?'}</h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {c.cta_subtitle || 'We feature reader-submitted city guides & hotel reviews every month. Pitch us your story.'}
          </p>
          <Link
            to="/list-your-property"
            className="inline-block bg-white text-[#FF0059] hover:bg-[#FFE5EE] font-black px-8 py-4 rounded-full transition-colors shadow-xl"
            data-testid="insights-cta-btn"
          >
            {c.cta_button || 'Submit your story →'}
          </Link>
        </div>
      </section>
    </div>
  );
};
